import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { isBctStaff, useAuthStore } from '@/stores/auth';
import { OnboardingChecklist } from './OnboardingChecklist';

/**
 * Self-contained mount point for OnboardingChecklist.
 * Fetches checklist state from the onboarding API and provides
 * completion/dismiss handlers. Mount inside AppLayout (needs router context).
 */
export function OnboardingChecklistMount() {
  const { user, isAuthenticated } = useAuthStore();
  const [completedItems, setCompletedItems] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  const shouldHide = !user || !isAuthenticated || isBctStaff(user.role);

  // Fetch checklist state on mount
  useEffect(() => {
    if (shouldHide) return;

    async function loadState() {
      try {
        const response = await api.get<{ tours?: Record<string, { completed?: boolean }> }>(
          '/users/me/onboarding',
        );
        const tours = response.data?.tours || {};
        // Extract checklist entries (stored as "checklist:<itemId>")
        const items: Record<string, boolean> = {};
        for (const [key, value] of Object.entries(tours)) {
          if (key.startsWith('checklist:')) {
            const itemId = key.replace('checklist:', '');
            items[itemId] = !!value.completed;
          }
        }
        setCompletedItems(items);
      } catch {
        // Silently fail — don't show checklist if state can't load
      }
      setLoaded(true);
    }

    loadState();
  }, [shouldHide]);

  const handleCompleteItem = useCallback(async (itemId: string) => {
    setCompletedItems((prev) => ({ ...prev, [itemId]: true }));
    try {
      await api.patch('/users/me/onboarding', {
        tourId: `checklist:${itemId}`,
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
  }, []);

  const handleDismiss = useCallback(async () => {
    setCompletedItems((prev) => ({ ...prev, dismissed: true }));
    try {
      await api.patch('/users/me/onboarding', {
        tourId: 'checklist:dismissed',
        action: 'complete',
        version: 1,
      });
    } catch {
      // Silently fail
    }
  }, []);

  if (shouldHide || !loaded || completedItems.dismissed) return null;

  return (
    <OnboardingChecklist
      completedItems={completedItems}
      onCompleteItem={handleCompleteItem}
      onDismiss={handleDismiss}
    />
  );
}
